import type { ApplicationDocument, ProductionDocument } from '@/types'
import type { ParticipantPackageConfig } from '@/types/content.types'
import { configureParticipantPackageFn } from '@/lib/firebase/functions'
import {
  db,
  typedCollection,
  typedDoc,
  newTypedDoc,
  getCollection,
  getDocument,
  updateDocument,
  deleteDocument,
  writeBatch,
  serverTimestamp,
  orderBy,
  where,
  deleteField,
  onSnapshot,
} from '@/lib/firebase/firestore'
import type { ProductionFormValues } from '../types'
import { createUserFacingError } from '@/lib/errors'

const col = () => typedCollection<ProductionDocument>('productions')
const doc = (id: string) => typedDoc<ProductionDocument>('productions', id)
const applicationDoc = (id: string) => typedDoc<ApplicationDocument>('applications', id)

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function toPayload(data: ProductionFormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: data.name,
    slug: data.slug,
    type: data.type,
    status: data.status,
    venue: data.venue,
    startDate: data.startDate,
    isArchived: false,
  }
  if (data.endDate) payload.endDate = data.endDate
  if (data.driveLink) payload.driveLink = data.driveLink
  if (data.intakeApplicationId) payload.intakeApplicationId = data.intakeApplicationId
  if (data.techApplicationId) payload.techApplicationId = data.techApplicationId
  return payload
}

export const adminProductionsService = {
  subscribeById(
    id: string,
    onNext: (production: ProductionDocument | null) => void,
    onError: (error: unknown) => void
  ) {
    return onSnapshot(doc(id), (snapshot) => onNext(snapshot.data() ?? null), onError)
  },
  async getAll(): Promise<Array<ProductionDocument & { id: string }>> {
    return getCollection(col(), orderBy('createdAt', 'desc'))
  },

  async getById(id: string): Promise<ProductionDocument | null> {
    return getDocument(doc(id))
  },

  async getBySlug(slug: string): Promise<(ProductionDocument & { id: string }) | null> {
    const results = await getCollection(col(), where('slug', '==', slug))
    return results[0] ?? null
  },

  async getByIntakeApplicationId(
    applicationId: string
  ): Promise<(ProductionDocument & { id: string }) | null> {
    const results = await getCollection(col(), where('intakeApplicationId', '==', applicationId))
    return results[0] ?? null
  },

  async create(data: ProductionFormValues): Promise<string> {
    const slug = data.slug || slugify(data.name)
    // Slug uniqueness — admins won't remember every prior slug, so we check.
    const existing = await this.getBySlug(slug)
    if (existing) {
      throw createUserFacingError(`A production with slug "${slug}" already exists.`)
    }
    if (data.intakeApplicationId) {
      const application = await getDocument(applicationDoc(data.intakeApplicationId))
      if (!application)
        throw createUserFacingError('The selected intake application could not be found.')
      if ((application.applicationType ?? 'general') !== 'production_intake') {
        throw createUserFacingError(
          'Only a Production Intake application can be linked to a production.'
        )
      }
      if (application.productionId) {
        throw createUserFacingError(
          'That intake application is already linked to another production.'
        )
      }
    }
    const payload = {
      ...toPayload({ ...data, slug }),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const ref = newTypedDoc(col())
    const batch = writeBatch(db)
    batch.set(ref, payload as unknown as ProductionDocument)

    // Bidirectional link: if the production is linked to an intake application,
    // write the production id back onto the application so the two stay in
    // sync regardless of which side initiated the link.
    if (data.intakeApplicationId) {
      batch.update(applicationDoc(data.intakeApplicationId), {
        productionId: ref.id,
        updatedAt: serverTimestamp(),
      })
    }

    await batch.commit()

    return ref.id
  },

  async update(id: string, data: Partial<ProductionFormValues>): Promise<void> {
    const current = await getDocument(doc(id))
    if (!current) throw createUserFacingError('Production not found.')

    if (data.slug) {
      const existing = await this.getBySlug(data.slug)
      if (existing && existing.id !== id) {
        throw createUserFacingError(`A production with slug "${data.slug}" already exists.`)
      }
    }
    const patch: Record<string, unknown> = {
      ...data,
      updatedAt: serverTimestamp(),
    }
    // Optional-FK fields use empty string in the form to mean "clear it"
    if ('intakeApplicationId' in data) {
      patch.intakeApplicationId = data.intakeApplicationId || deleteField()
    }
    if ('techApplicationId' in data) {
      patch.techApplicationId = data.techApplicationId || deleteField()
    }

    const nextIntakeApplicationId =
      'intakeApplicationId' in data
        ? (data.intakeApplicationId ?? '')
        : (current.intakeApplicationId ?? '')
    if (nextIntakeApplicationId) {
      const application = await getDocument(applicationDoc(nextIntakeApplicationId))
      if (!application)
        throw createUserFacingError('The selected intake application could not be found.')
      if ((application.applicationType ?? 'general') !== 'production_intake') {
        throw createUserFacingError(
          'Only a Production Intake application can be linked to a production.'
        )
      }
      if (application.productionId && application.productionId !== id) {
        throw createUserFacingError(
          'That intake application is already linked to another production.'
        )
      }
    }

    const batch = writeBatch(db)
    batch.update(doc(id), patch)
    if (current.intakeApplicationId && current.intakeApplicationId !== nextIntakeApplicationId) {
      const previousApplication = await getDocument(applicationDoc(current.intakeApplicationId))
      if (previousApplication?.productionId === id) {
        batch.update(applicationDoc(current.intakeApplicationId), {
          productionId: deleteField(),
          updatedAt: serverTimestamp(),
        })
      }
    }
    if (nextIntakeApplicationId) {
      batch.update(applicationDoc(nextIntakeApplicationId), {
        productionId: id,
        updatedAt: serverTimestamp(),
      })
    }
    await batch.commit()
  },

  async setArchived(id: string, isArchived: boolean): Promise<void> {
    await updateDocument(doc(id), {
      isArchived,
      updatedAt: serverTimestamp(),
    } as unknown as Partial<ProductionDocument>)
  },

  async updateParticipantPackage(
    id: string,
    participantPackage: ParticipantPackageConfig,
    intendedParticipantSlots: number
  ): Promise<void> {
    const current = await getDocument(doc(id))
    if (!current) throw createUserFacingError('Production not found.')

    if (
      current.participantRosterState === 'locked' ||
      current.participantRosterState === 'public_sales_open'
    ) {
      throw createUserFacingError(
        'Participant package settings cannot change after the roster is locked.'
      )
    }

    // The callable validates and atomically writes the production plus every
    // event mapping, so there is no interval where a newly linked performance
    // can remain open to public checkout.
    await configureParticipantPackageFn({
      productionId: id,
      config: participantPackage,
      intendedParticipantSlots,
    })
  },

  async delete(id: string): Promise<void> {
    const production = await getDocument(doc(id))
    if (!production?.intakeApplicationId) {
      await deleteDocument(doc(id))
      return
    }

    const application = await getDocument(applicationDoc(production.intakeApplicationId))
    const batch = writeBatch(db)
    batch.delete(doc(id))
    if (application?.productionId === id) {
      batch.update(applicationDoc(production.intakeApplicationId), {
        productionId: deleteField(),
        updatedAt: serverTimestamp(),
      })
    }
    await batch.commit()
  },

  slugify,
}
